import { useMemo } from "react";
import { SWAP_CONVENTION_CURRENCIES } from "@/lib/businessRules/swapConventionsRule";
import {
  IReferenceCode,
  ReferenceList,
} from "@/lib/interfaces and Enums/ENUMReferenceList";
import { ReferenceDataStatus } from "@/lib/interfaces and Enums/ENUMReferenceDataStatus";
import {
  SWAP_BUSINESS_DAYS,
  SWAP_DAY_COUNTS,
  SWAP_FREQUENCIES,
} from "@/lib/interfaces and Enums/ENUMSwapTradeMessage";
import referenceListsStore, {
  returnReferenceListEntry,
} from "@/store/referenceListsStore";
import { returnValidatedOptions } from "./referenceListOptions";

// The Swap / Swaption dropdown options. Read-only: the ticket shells and the "+ Leg"
// button start the load, so this never touches the network.

export interface ISwapReferenceLists {
  dayCounts: string[];
  businessDays: string[];
  frequencies: string[];
  currencies: string[];
  calendars: IReferenceCode[];
  isCalendarListReady: boolean;
}

export function returnSwapReferenceLists(
  lists: ReturnType<typeof referenceListsStore.getState>["lists"],
): ISwapReferenceLists {
  const loaded = (list: ReferenceList): IReferenceCode[] | null => {
    const entry = returnReferenceListEntry(lists, list);
    return entry.status === ReferenceDataStatus.Loaded ? entry.items : null;
  };
  const calendars = loaded(ReferenceList.Calendars);
  return {
    dayCounts: returnValidatedOptions(SWAP_DAY_COUNTS, loaded(ReferenceList.DayCounts)),
    businessDays: returnValidatedOptions(
      SWAP_BUSINESS_DAYS,
      loaded(ReferenceList.BusinessDayConventions),
    ),
    frequencies: returnValidatedOptions(SWAP_FREQUENCIES, loaded(ReferenceList.Frequencies)),
    // Every API currency, ours first (spec B17). A floating leg with no rates is blocked
    // at the Reference Rate instead (LEG_CREATION_PLAN Q25).
    currencies: returnValidatedOptions(
      SWAP_CONVENTION_CURRENCIES,
      loaded(ReferenceList.Currencies),
    ),
    calendars: calendars ?? [],
    isCalendarListReady: !!calendars && calendars.length > 0,
  };
}

export function useSwapReferenceLists(): ISwapReferenceLists {
  const lists = referenceListsStore((state) => state.lists);
  return useMemo(() => returnSwapReferenceLists(lists), [lists]);
}
